import { ResourceItem } from 'sos-common/js/commonTypes';

import {
    FileVM,
    FileStatus,
} from '../FileAPIClient/Api.types';

export interface FileUploadProps {
    pageHeading: string;
    pageBody: string;
    pageLinkMyCases: string;
    fileSessionId: string;
    translation: ResourceItem[];
}

export enum FileTrackerStatusEnum {
    None = 0,
    Preparing = 1,
    Prepared = 2,
    Queued = 3,
    Uploading = 4,
    UploadingPaused = 5,
    Completed = 6,
    Error = 7,
    Sent = 8,
}

export class UploadFileTracker {
    constructor(fil: File | null, storedFile: FileVM | null) {
        this.browserFile = fil;
        this.storedFile = storedFile;
        if (!storedFile) {
            this.status = FileTrackerStatusEnum.None;
            return;
        }

        this.checksum = storedFile.checksum;
        this.currentFilFragmentCount = storedFile.transferedFragments;
        if (storedFile.state.find(x => x === FileStatus.Sent)) {
            this.status = FileTrackerStatusEnum.Sent;
        }
        else if (storedFile.state.find(x => x === FileStatus.Uploaded)) {
            this.status = FileTrackerStatusEnum.Completed;
        }
        else {
            this.status = FileTrackerStatusEnum.UploadingPaused;
        }
    }

    get hasBrowserFile(): boolean {
        if (this.browserFile) return true;
        else return false;
    }

    get name(): string {
        if (this.storedFile) {
            return this.storedFile.name;
        } else if (this.browserFile) {
            return this.browserFile.name;
        } else {
            return '';
        }
    }

    get size(): number {
        if (this.storedFile) {
            return this.storedFile.size;
        } else if (this.browserFile) {
            return this.browserFile.size;
        } else {
            return 0;
        }
    }

    get progress(): number {
        if (!this.storedFile) {
            return 0;
        }
        const calculated = (this.currentFilFragmentCount / this.totalFilFragmentCount) * 100;
        const rounded = Math.round(calculated);
        return Math.max(0, Math.min(rounded, 100));
    };

    get totalFilFragmentCount(): number {
        if (!this.storedFile) {
            return 1; // 0 här kan leda till division med 0
        }
        const totalFileSize = this.size;
        if (totalFileSize === 0) {
            return 1; // 0 här kan leda till division med 0
        }
        var fragmentSize = this.storedFile.fileFragmentSize;
        return totalFileSize % fragmentSize === 0
            ? totalFileSize / fragmentSize
            : Math.floor(totalFileSize / fragmentSize) + 1;
    };

    get hasInfo(): boolean {
        if (this.infoMsg && this.infoMsg.length > 0) {
            return true;
        } else {
            return false;
        }
    }

    get hasError(): boolean {
        if (this.errorMsg && this.errorMsg.length > 0) {
            return true;
        } else {
            return false;
        }
    }

    clearMessages(): void {
        this.infoMsg = '';
        this.errorMsg = '';
    }

    async calculateChecksum(): Promise<string> {
        if (!this.browserFile) {
            throw new Error('BrowserFile does not exist - cannot calculate checksum');
        }

        // Read the file content as ArrayBuffer
        const arrayBuffer = await this.browserFile.arrayBuffer();

        // Generate SHA-1 hash using Web Crypto API
        const hashBuffer = await crypto.subtle.digest('SHA-1', arrayBuffer);

        // Convert the hash to base64 string
        const hashArray = new Uint8Array(hashBuffer);
        const hashBase64 = btoa(String.fromCharCode(...hashArray));

        return hashBase64;
    }

    currentFilFragmentCount: number = 0;
    status: FileTrackerStatusEnum;
    browserFile: File | null;
    storedFile: FileVM | null;
    errorMsg: string = '';
    infoMsg: string = '';
    checksum: string | null = null;
    checksumProgress: number = 0;
}